import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { XCricle, Refresh, CancelIcon } from '../../assets/images';
import { UserRole } from '../../containers/navigation-bar/roles';
import { useCancelEligibilityFile, useEligibilityErrorFile, useEligibilityFile } from '../../hooks/useEligibility';
import useAuth from '../../hooks/useAuth';

type props = {
  data: any;
  clientId: number;
  tableType: string;
  handleEligibilityFileViewModel: (id: number, type: string) => void;
  isACtiveAndScheduleShow?: boolean;
  handleDeleteEligibility?: (id: number) => void;
  handleProcessEligibility?: (id: number) => void;
  handleReplaceEligibility?: (id: number, date: string) => void;
  handleTranferEligibility?: (id: number) => void;
  handleRefresh?: (type: string) => void;
};
const EligibilityTableList: React.FunctionComponent<props> = (props): JSX.Element => {
  const { data, clientId } = props;
  const { downloadFile, isFetching: isFetchingFile } = useEligibilityFile();
  const { downloadErrorFile, isFetching: isFetchingErrorFile } = useEligibilityErrorFile();
  const cancelMutation = useCancelEligibilityFile();
  const { roleType: userType } = useAuth();
  const [showOptimisticProcessing, setShowOptimisticProcessing] = useState<boolean>(false);

  const cancelProcess = (eligibilityProcessId: number) => {
    cancelMutation.mutate(
      { clientId: clientId, datasetId: eligibilityProcessId },
      {
        onSettled: () => {
          props.handleRefresh && props.handleRefresh(props.tableType);
          setShowOptimisticProcessing(false);
        },
      },
    );
  };

  const handleDownloadEligibility = (eligibilityDatasetId: number) => {
    downloadFile(clientId, eligibilityDatasetId);
  };

  const handleDownloadErrorFile = (eligibilityDatasetId: number) => {
    downloadErrorFile(eligibilityDatasetId);
  };

  // Reset optimistic processing state on reload so that item.status can be the source of truth. We set it to true on click to give the user immediate feedback.
  // I believe that in Stage the `?active` fetch comes back too quickly and still shows the file as `item.status = validation_pass` when we're expecting `item.status = in_progress` and seeing that in lower environments
  useEffect(() => {
    setShowOptimisticProcessing(false);
  }, []);

  return (
    <div>
      <div className="px-7">
        <table className="table-row-format relative w-full table-fixed border-collapse">
          <thead className="h-full bg-white">
            <tr className="w-full px-7 text-left">
              {props.tableType === 'scheduled' && userType === UserRole.client_admin ? (
                <>
                  <th className="sticky top-0 mb-4 h-full w-1/2 bg-white px-2 py-1 font-normal">Live Date</th>
                  <th className="w-1/2"></th>
                </>
              ) : (
                <>
                  <th className="sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-1 font-normal">Members</th>
                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-2 text-base font-normal">
                    Live Date
                  </th>

                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-2 text-base font-normal">
                    New
                  </th>

                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-1 text-base font-normal">
                    Existing
                  </th>
                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-2 text-base font-normal">
                    Removed
                  </th>
                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-2 text-base font-normal">
                    Invalid
                  </th>
                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-2 text-base font-normal">
                    Uploaded / Pulled
                  </th>
                  {props.isACtiveAndScheduleShow && (
                    <>
                      <th
                        className={`${
                          userType === UserRole.admin ? 'w-2/5' : 'w-20'
                        } text-gray-dark sticky top-0 mb-4 h-full bg-white px-2 py-2 text-base font-normal`}
                      ></th>
                    </>
                  )}
                  <th className="text-gray-dark sticky top-0 mb-4 h-full w-1/12 bg-white px-2 py-2 text-base font-normal">
                    {userType === UserRole.admin ? (
                      <button
                        type="button"
                        className="flex h-full w-full items-center justify-center"
                        onClick={() => props.handleRefresh && props.handleRefresh(props.tableType)}
                      >
                        <img src={Refresh} alt="refresh-icon" className="h-6 w-6" />
                      </button>
                    ) : null}
                  </th>
                  <th className="w-2/12"></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            <tr className="h-2"></tr>
            {data?.length ? (
              data.map((item: any) => {
                return (
                  <tr
                    key={item.id}
                    className="text-gray-dark h-12 space-y-6 rounded border-4 border-y border-white text-left"
                    data-testid={`eligibility-row-${item.id}`}
                  >
                    {props.tableType === 'scheduled' && userType === UserRole.client_admin ? (
                      <>
                        <td
                          className="blue-primary border-bottom w-2/12 py-1"
                          data-testid={`scheduled-live-date-${item.id}`}
                        >
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold">
                              {props.isACtiveAndScheduleShow
                                ? moment(item?.billing_cycle).format('YYYY-MM-DD')
                                : moment(item.completed_at).format('YYYY-MM-DD')}
                            </p>
                          </div>
                        </td>
                        <td className="border-bottom w-2/12 py-1" data-testid={`scheduled-delete-button-${item.id}`}>
                          <button
                            type="button"
                            className="ml-2 flex items-center"
                            onClick={() => props.handleDeleteEligibility && props.handleDeleteEligibility(item.id)}
                          >
                            <img src={XCricle} alt="x-circle" />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td
                          className="text-gray-dark text-bold border-bottom w-2/12 py-1 font-bold"
                          data-testid={`members-count-${item.id}`}
                        >
                          {item.validation_report?.record_count}
                        </td>
                        <td className="blue-primary border-bottom w-2/12 py-1" data-testid={`live-date-${item.id}`}>
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold">
                              {props.isACtiveAndScheduleShow
                                ? moment(item?.billing_cycle).format('YYYY-MM-DD')
                                : moment(item.completed_at).format('YYYY-MM-DD')}
                            </p>
                          </div>
                        </td>
                        <td className="border-bottom w-2/12 py-1" data-testid={`new-count-${item.id}`}>
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold">
                              {item.validation_report?.added_members_count}
                            </p>
                          </div>
                        </td>
                        <td className="border-bottom w-2/12 py-1" data-testid={`existing-count-${item.id}`}>
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold">
                              {item.validation_report?.existing_members_count}
                            </p>
                          </div>
                        </td>
                        <td className="border-bottom w-2/12 py-1" data-testid={`removed-count-${item.id}`}>
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold">
                              {item.validation_report?.removed_members_count}
                            </p>
                          </div>
                        </td>
                        <td className="border-bottom w-2/12 py-1" data-testid={`invalid-count-${item.id}`}>
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold">
                              {item.validation_report?.invalid_records_count}
                            </p>
                          </div>
                        </td>
                        <td className="border-bottom w-2/12 py-1" data-testid={`process-type-${item.id}`}>
                          <div className="flex">
                            <p className="text-gray-dark text-bold pr-2 font-bold capitalize">{item.process_type}</p>
                          </div>
                        </td>
                        {props.isACtiveAndScheduleShow && (
                          <>
                            <td className={`border-bottom py-1`} data-testid={`process-button-${item.id}`}>
                              <div className="flex w-full items-center">
                                {userType === UserRole.admin ? (
                                  <>
                                    {item.status === 'transfer_pending' ? (
                                      <button
                                        className="btn-darkblue px-2 py-1 text-sm"
                                        onClick={() =>
                                          props.handleTranferEligibility && props.handleTranferEligibility(item.id)
                                        }
                                        data-testid="import-button"
                                      >
                                        IMPORT
                                      </button>
                                    ) : item.status === 'processing_failed' ? (
                                      <>
                                        <span className="text-sm font-medium text-red-600">Processing Failed</span>
                                        <button
                                          className="btn-darkblue whitespace-nowrap px-2 py-1 text-sm"
                                          type="button"
                                          data-testid="error-file-button"
                                          onClick={() => {
                                            handleDownloadErrorFile(item.id);
                                          }}
                                          disabled={isFetchingErrorFile}
                                        >
                                          {isFetchingErrorFile ? 'Downloading...' : 'Error File'}
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn-darkblue whitespace-nowrap px-2 py-1 text-sm"
                                          onClick={() => {
                                            setShowOptimisticProcessing(true);
                                            if (props.handleProcessEligibility) {
                                              props.handleProcessEligibility(item.id);
                                            }
                                          }}
                                          disabled={item.status === 'in_progress'}
                                          data-testid="process-button"
                                        >
                                          {item.status === 'in_progress' || showOptimisticProcessing
                                            ? `PROCESSING...${item.percentage_complete ? `: ${item.percentage_complete}` : ''}`
                                            : 'PROCESS'}
                                        </button>
                                        <button
                                          className="btn-darkblue ml-2 px-2 py-1 text-sm"
                                          type="button"
                                          onClick={() =>
                                            props.handleReplaceEligibility &&
                                            props.handleReplaceEligibility(item.id, item.scheduled_at)
                                          }
                                          data-testid="eligibility-table-list-replace-button"
                                        >
                                          REPLACE
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : null}
                                {item.percentage_complete?.includes('%') || showOptimisticProcessing ? (
                                  <span className="flex flex-col items-center justify-center pl-2">
                                    <label htmlFor="cancelButton">Reset</label>
                                    <button
                                      id="cancelButton"
                                      className="ml-2 flex items-center"
                                      onClick={() => cancelProcess(item.id)}
                                      data-testid="cancel-eligibility-processing-button"
                                    >
                                      <img src={CancelIcon} alt="cancel-icon" className="h-6 w-6" />
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="ml-2 flex items-center"
                                    onClick={() =>
                                      props.handleDeleteEligibility && props.handleDeleteEligibility(item.id)
                                    }
                                    data-testid="delete-button"
                                  >
                                    <img src={XCricle} alt="x-circle" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        )}
                        <td className="border-bottom w-1/12 py-1">
                          {!['transfer_pending', 'processing_failed'].includes(item.status) && (
                            <button
                              className="blue-primary"
                              onClick={() => props.handleEligibilityFileViewModel(item.id, props.tableType)}
                              data-testid="eligibility-table-list-view-button"
                            >
                              View
                            </button>
                          )}
                        </td>
                        <td className="border-bottom w-1/12 py-1">
                          {item.csv_path && (
                            <button
                              className="blue-primary ml-2"
                              onClick={() => handleDownloadEligibility(item.id)}
                              data-testid="eligibility-table-list-download-button"
                              disabled={isFetchingFile}
                            >
                              {isFetchingFile ? 'Downloading...' : 'Download'}
                            </button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="ext-gray-dark h-20 text-center" data-testid="no-eligibility-message">
                  {props.tableType === 'active' && 'No Active Eligibility'}
                  {props.tableType === 'scheduled' && 'No Scheduled Eligibility'}
                  {props.tableType === 'historical' && 'No Historic Eligibility'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default EligibilityTableList;
